import { geometryOutline, parseLocationGeometry, pointsBounds, polygonCentroid } from './geometry'
import type { Location, LocationPathSegment } from './schemas'

/**
 * Mini-plan 2D d'une pièce, vue du dessus, tiré de la géométrie de la vue 3D (sans dessin : le site le trace en
 * SVG, l'appli avec `react-native-svg`).
 * Repère du lieu en mètres (x vers la droite, z vers le bas de l'écran) → points en pixels dans un cadre donné.
 */

export type ScreenPoint = { x: number; y: number }
/** `mark` : meuble d'une proposition de l'assistant (`added` : ajouté ou déplacé, `removed` : retiré). */
export type MiniPlanShape = { id: string; name: string; points: ScreenPoint[]; highlight: boolean; mark?: 'added' | 'removed' }
export type MiniPlan = {
  width: number
  height: number
  room: ScreenPoint[]
  furniture: MiniPlanShape[]
  /** Centre du meuble mis en avant (repère « c'est ici »), s'il y en a un. */
  pin: ScreenPoint | null
}

/**
 * Quelle pièce dessiner pour un chemin, et quel meuble y mettre en avant : la dernière pièce du chemin, et
 * l'étape juste après elle (le meuble qui contient l'objet, même rangé plus bas dans un tiroir).
 */
export function planTarget(
  path: Pick<LocationPathSegment, 'id' | 'type'>[],
): { roomId: string; highlightId: string | null } | null {
  let roomIndex = -1
  path.forEach((s, i) => {
    if (s.type === 'room') roomIndex = i
  })
  if (roomIndex < 0) return null
  return { roomId: path[roomIndex]!.id, highlightId: path[roomIndex + 1]?.id ?? null }
}

/**
 * Plan de `room` avec ses meubles, ajusté dans `maxWidth` × `maxHeight` (proportions gardées, marge `padding`).
 * `null` si la pièce n'a pas de géométrie (pas encore dessinée dans la vue 3D) : l'écran n'affiche alors rien.
 */
export function miniPlan(
  room: Pick<Location, 'geometry'>,
  furniture: (Pick<Location, 'id' | 'name' | 'geometry'> & { mark?: 'added' | 'removed' })[],
  highlightId: string | null,
  frame: { maxWidth: number; maxHeight: number; padding?: number },
): MiniPlan | null {
  const roomGeometry = parseLocationGeometry(room.geometry)
  if (!roomGeometry) return null
  const outline = geometryOutline(roomGeometry)
  const b = pointsBounds(outline)
  const spanX = Math.max(b.maxX - b.minX, 0.01)
  const spanZ = Math.max(b.maxZ - b.minZ, 0.01)
  const pad = frame.padding ?? 4
  const scale = Math.min((frame.maxWidth - 2 * pad) / spanX, (frame.maxHeight - 2 * pad) / spanZ)
  const width = round(spanX * scale + 2 * pad)
  const height = round(spanZ * scale + 2 * pad)
  const toScreen = (p: { x: number; z: number }): ScreenPoint => ({
    x: round(pad + (p.x - b.minX) * scale),
    y: round(pad + (p.z - b.minZ) * scale),
  })

  const shapes: MiniPlanShape[] = []
  let pin: ScreenPoint | null = null
  for (const f of furniture) {
    const g = parseLocationGeometry(f.geometry)
    if (!g) continue
    const pts = geometryOutline(g)
    const highlight = f.id === highlightId
    shapes.push({ id: f.id, name: f.name, points: pts.map(toScreen), highlight, ...(f.mark ? { mark: f.mark } : {}) })
    if (highlight) pin = toScreen(polygonCentroid(pts))
  }
  // Le meuble mis en avant, puis ceux d'une proposition, sont dessinés en dernier (par-dessus les autres).
  const rank = (s: MiniPlanShape) => (s.highlight ? 2 : s.mark ? 1 : 0)
  shapes.sort((a, b2) => rank(a) - rank(b2))
  return { width, height, room: outline.map(toScreen), furniture: shapes, pin }
}

/** Points au format de l'attribut SVG `points`. */
export const svgPoints = (pts: ScreenPoint[]) => pts.map((p) => `${p.x},${p.y}`).join(' ')

const round = (n: number) => Math.round(n * 10) / 10
